import {
  CheckIcon,
  DuplicateIcon,
  LocationIcon,
  PhoneIcon,
  StoreIcon,
  SwapIcon,
} from '@/components/icons';
import { useQueries, useQuery } from '@tanstack/react-query';
import { App, Dropdown, Tag, Typography, theme } from 'antd';
import { useMemo } from 'react';

import { getOpenNow, getPharmacy } from '@/api/pharmacies.api';
import type { OperatingStatus, Pharmacy, Uuid } from '@/api/types';
import { useActivePharmacy } from '@/auth/active-pharmacy';
import { queryKeys } from '@/lib/query-keys';
import { shortId } from '@/lib/format';

const { Text } = Typography;

const OPERATING: Record<OperatingStatus, { colour: string; label: string }> = {
  OPEN: { colour: 'success', label: 'Open' },
  BUSY: { colour: 'warning', label: 'Busy' },
  TEMPORARILY_UNAVAILABLE: { colour: 'warning', label: 'Paused' },
  CLOSED: { colour: 'default', label: 'Closed' },
};

/** "12 Rashbehari Ave, Kolkata 700029" — one line, in the order it is written on an envelope. */
function addressLine(pharmacy: Pharmacy): string {
  return [pharmacy.addressLine1, pharmacy.addressLine2, pharmacy.city, pharmacy.pincode]
    .filter(Boolean)
    .join(', ');
}

/**
 * Which branch is on screen.
 *
 * Records come from `GET /pharmacies/:id`, one request per assigned branch —
 * almost always exactly one. Fetching each by id rather than listing every
 * pharmacy keeps a staff account from pulling down the whole platform's
 * directory just to label its own header.
 *
 * The short id stays as the fallback while the record loads, and for an admin
 * override whose record may not resolve: a pharmacist with two branches needs
 * *something* stable to recognise, and a silent switch is far worse than an
 * ugly label.
 */
export function BranchSwitcher() {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { pharmacyId, assigned, isOverride, setPharmacyId } = useActivePharmacy();

  // Union rather than `assigned` alone, so an admin override still gets a name.
  const ids = useMemo(() => {
    const set = new Set(assigned);
    if (pharmacyId) set.add(pharmacyId);
    return [...set];
  }, [assigned, pharmacyId]);

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: queryKeys.pharmacy(id),
      queryFn: () => getPharmacy(id),
      // A branch is not renamed mid-shift; this is as static as data gets here.
      staleTime: 10 * 60_000,
      retry: false,
    })),
  });

  const records = useMemo(() => {
    const map = new Map<Uuid, Pharmacy>();
    ids.forEach((id, index) => {
      const record = results[index]?.data;
      if (record) map.set(id, record);
    });
    return map;
  }, [ids, results]);

  const openNow = useQuery({
    queryKey: queryKeys.openNow(pharmacyId ?? ''),
    queryFn: () => getOpenNow(pharmacyId!),
    enabled: false,
  });

  if (!pharmacyId) return null;

  const label = (id: Uuid) => records.get(id)?.name ?? shortId(id);
  const active = records.get(pharmacyId);

  /*
   * Costs no request: `AvailabilityPill` is mounted in the same header and
   * fetches this under the same key, so this reads its cache. `enabled` is off
   * because nothing here should be the thing that triggers the fetch — if the
   * pill is not mounted, the card falls back to the manual status.
   */
  const availability = openNow.data ? openNow.data.isOpen : null;

  /*
   * The branch card at the top of the thing it scopes.
   *
   * It used to be a chip in the page header, three feet of screen away from the
   * Stock and Bills links it governs. Here it reads as what it is: the context
   * everything below it is filtered by. Same control as the admin panel's scope
   * picker, down to the class names — the two apps sit on the same desk.
   */
  const trigger = (switchable: boolean) => (
    <button type="button" className="scope-trigger" aria-label="The branch you are working in">
      {active ? (
        <span className="scope-avatar">{active.name.charAt(0).toUpperCase()}</span>
      ) : (
        <span className="scope-avatar scope-avatar-empty">
          <StoreIcon />
        </span>
      )}
      <span className="min-w-0 flex-1 text-left">
        <span className={`scope-trigger-label ${active ? '' : 'tabular'}`}>
          {label(pharmacyId)}
        </span>
        <Text
          type={isOverride ? 'warning' : 'secondary'}
          className="block truncate text-[10.5px]"
        >
          {isOverride ? 'Admin view — opened by id' : 'Working in'}
        </Text>
      </span>
      {/*
        A caret on a card that opens nothing is a lie. With one branch this is a
        label you can hover for the details, so it does not get one.
      */}
      {switchable ? <SwapIcon className="scope-caret" /> : null}
    </button>
  );

  /*
   * The id was all this used to say, and an id is the one fact about a branch
   * nobody can act on. What someone actually needs to confirm before posting
   * stock is that this is the right *shop* — its address, its phone, whether it
   * is open, and whether its licence is verified, since an unverified licence
   * means it cannot legally trade at all.
   */
  /*
   * The same panel the account menu and the availability pill draw.
   *
   * All three hang off this one column, are read in the same glance, and answer
   * the same shape of question — what is true here, and what can I do about it.
   * Three vocabularies for that was what made the sidebar and the header read
   * as different products.
   *
   * The id was all this used to say, and an id is the one fact about a branch
   * nobody can act on. What someone actually needs to confirm before posting
   * stock is that this is the right *shop*: its address, its phone, whether it
   * is taking orders, and whether its licence is verified — since an unverified
   * licence means it cannot legally trade at all.
   */
  const detail = active ? (
    <>
      <div className="panel-head">
        <span className="scope-avatar shrink-0">{active.name.charAt(0).toUpperCase()}</span>
        <div className="min-w-0">
          <div className="panel-title display">{active.name}</div>
          {active.legalName && active.legalName !== active.name ? (
            <span className="panel-note">{active.legalName}</span>
          ) : null}
        </div>
      </div>

      <div className="panel-section">
        <div className="panel-fact">
          <LocationIcon className="panel-fact-icon" />
          <span>{addressLine(active)}</span>
        </div>
        <div className="panel-fact">
          <PhoneIcon className="panel-fact-icon" />
          <span className="tabular">{active.phone}</span>
        </div>

        <div className="panel-tags">
          {/*
            The SAME answer the availability pill gives, from the same cached
            query — not `operatingStatus`, which is the platform's manual flag
            and one of four inputs to it.

            They disagree routinely: a shop whose flag reads OPEN is still shut
            at 11pm. This card said "Open" while the pill in the header said
            "Not taking orders", three inches apart, and the one that matters to
            a customer is the pill's.
          */}
          <Tag
            color={
              availability === null
                ? OPERATING[active.operatingStatus].colour
                : availability
                  ? 'success'
                  : 'warning'
            }
            bordered={false}
            className="m-0!"
          >
            {availability === null
              ? OPERATING[active.operatingStatus].label
              : availability
                ? 'Taking orders'
                : 'Not taking orders'}
          </Tag>
          {/*
            A shop with no verified, unexpired licence cannot legally trade, and
            the backend computes this rather than trusting a stored flag — so it
            is worth more room than a quiet grey label.
          */}
          {active.hasVerifiedLicense ? (
            <Tag color="success" bordered={false} className="m-0!">
              <CheckIcon /> Licence verified
            </Tag>
          ) : (
            <Tag color="error" bordered={false} className="m-0!">
              Licence not verified
            </Tag>
          )}
          {active.acceptsOnlineOrders ? null : (
            <Tag bordered={false} className="m-0!">
              Counter only
            </Tag>
          )}
          {active.supportsColdChain ? (
            <Tag bordered={false} className="m-0!">
              Cold chain
            </Tag>
          ) : null}
        </div>

        {isOverride ? (
          <Text type="warning" className="mt-2.5 block text-[11.5px] leading-snug">
            Opened by id rather than by staff assignment. Everything you do here is still recorded
            against your account.
          </Text>
        ) : null}
      </div>

      {/*
        The full id used to be printed here, under every branch, for everyone.
        It is the one fact about a shop nobody standing at the counter can act
        on — thirty-six characters of noise under the address and the phone
        number, which are the two things they came here to check.

        It is still worth reaching when a branch has to be named to support, so
        it is on a control rather than on the wall — and that control is the
        same row the account panel signs out with.
      */}
      <div className="panel-actions">
        <button
          type="button"
          className="panel-action"
          onClick={() => {
            void navigator.clipboard?.writeText(pharmacyId).then(
              () => message.success('Branch id copied'),
              () => message.error('Could not copy the id'),
            );
          }}
        >
          <span className="panel-action-icon">
            <DuplicateIcon />
          </span>
          Copy branch id
        </button>
      </div>
    </>
  ) : (
    /*
     * No record. The id IS how this branch was opened, so here it is shown
     * outright rather than hidden behind a copy control.
     */
    <>
      <div className="panel-head">
        <span className="scope-avatar scope-avatar-empty shrink-0">
          <StoreIcon />
        </span>
        <div className="min-w-0">
          <div className="panel-title">{isOverride ? 'Opened by id' : 'This branch'}</div>
          <span className="panel-note tabular break-all">{pharmacyId}</span>
        </div>
      </div>
      <div className="panel-section">
        <p className="panel-hint mt-0!">Its record could not be loaded, so only the id is known.</p>
      </div>
    </>
  );

  /*
   * `Dropdown` in both cases, never `Popover`.
   *
   * Popover paints its own inner surface and an arrow, so `.panel`'s border and
   * shadow would have landed inside a second set of them — the account menu and
   * the availability pill are both Dropdowns for the same reason.
   */
  if (assigned.length < 2) {
    // Placed to the side rather than below: in a sidebar, a panel dropping down
    // covers the navigation it is the context for.
    return (
      <Dropdown placement="rightTop" trigger={['hover']} mouseEnterDelay={0.3} popupRender={() => <div className="panel">{detail}</div>}>
        {trigger(false)}
      </Dropdown>
    );
  }

  return (
    <Dropdown
      placement="bottomRight"
      popupRender={() => (
        /*
         * The branch card, and under it the list to change it — one panel, the
         * switcher sitting in the same slot the account menu signs out from.
         */
        <div className="panel">
          {detail}
          <div className="panel-actions">
            <span className="panel-label px-2!">Switch branch</span>
            {assigned.map((id) => (
              <button
                key={id}
                type="button"
                className="panel-action"
                onClick={() => setPharmacyId(id)}
                style={id === pharmacyId ? { color: token.colorPrimary } : undefined}
              >
                <span className="panel-action-icon">
                  <StoreIcon />
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {label(id)}
                  {records.get(id) ? (
                    <span className="text-ink-muted block truncate text-[11px]">
                      {records.get(id)!.city}
                    </span>
                  ) : null}
                </span>
                {id === pharmacyId ? <CheckIcon /> : null}
              </button>
            ))}
          </div>
        </div>
      )}
    >
      {trigger(true)}
    </Dropdown>
  );
}
